import Button, { ButtonProps } from '@/components/elements/forms/buttons/Button/Button';
import LoadingPlaceHolder from '@/components/elements/LoadingPlaceholder';
import CardWrapper from '@/components/modules/CardWrapper';

import { HeaderButtonIdentifier } from '@/components/elements/HeaderButton/HeaderButton.types';
import Snackbar from '@/components/elements/notifications/Snackbar/Snackbar';
import Icon from '@/components/icons/Icon';
import CheckoutViewLayout from '@/components/layouts/CheckoutViewLayout';
import { ThreeDSComponent } from '@/components/modules/adyen';
import CheckoutDetails, { CheckoutDetailsProps } from '@/components/modules/checkout/CheckoutDetails/CheckoutDetails';
import CheckoutModal from '@/components/modules/checkout/CheckoutModal/CheckoutModal';
import {
  CheckoutModalProvider,
  useCheckoutModal,
} from '@/components/modules/checkout/CheckoutModal/CheckoutModal.hooks';
import CheckoutTerms from '@/components/modules/checkout/CheckoutTerms/CheckoutTerms';
import KlarnaOptionsModal from '@/components/modules/checkout/KlarnaOptionsModal';
import PromotedPaymentMethods from '@/components/modules/checkout/PromotedPaymentMethods/PromotedPaymentMethods';
import WalleyPhoneModal from '@/components/modules/checkout/WalleyPhoneModal';
import CreditCardModal from '@/components/modules/modals/CreditCardModal';
import Modal from '@/components/modules/modals/redesign/Modal/Modal';
import SwishModal from '@/components/modules/modals/SwishModal';
import GoBack from '@/components/modules/pages/bokningar/GoBack';
import FloatingCTAWrapper from '@/components/modules/pages/booking/checkout/FloatingCTAWrapper';
import UserLogin from '@/components/templates/checkout/UserLogin/UserLogin';
import { SCREEN_NAME } from '@/constants/analytics';
import { CHECKOUT_PAYMENT_METHOD } from '@/constants/checkout';
import { AdyenCheckoutComponent, useOnCompleteAdyenCheckout } from '@/features/payments';
import {
  usePaymentMethodsContext,
  UsePaymentMethodsProvider,
  WithPaymentMethodsLoaded,
} from '@/features/payments/hooks/usePaymentMethods';
import { KlarnaVariant } from '@/features/payments/types/adyen.types';
import { getValidKlarnaBrands } from '@/features/payments/utils/paymentConfig.utils';
import { classnames, promiseWrapper, url } from '@/helpers';
import { getThreeDSecureRedirectResultIfAny } from '@/helpers/adyen';
import {
  getBookingSubmitLabel,
  getCheckoutSubmitIconVariantIfAny,
  getCheckoutTerms,
  getCheckoutTermsVariant,
  hasPaymentMethod,
  isFullyPaidWithGiftcards,
} from '@/helpers/checkout';
import { getSummaryProperties, paymentActivationFlowTrackingSession } from '@/helpers/checkoutFlowTrackingSession';
import {
  mapConfirmedBookingInfoGiftcards,
  mapConfirmedBookingInfoServices,
  mapConfirmedBookingToCheckoutDetailsProps,
} from '@/helpers/confirmation';
import { isBokadirektPayCheckout } from '@/helpers/placeHelperTS';
import { loggedUser, validatePhoneNumber } from '@/helpers/userHelper';
import withWalletPayment from '@/hoc/withWalletPayment';
import { useAppSelector } from '@/hooks';
import { useCardsManager, UseCardsProvider } from '@/hooks/adyen/useCards';
import { useCardPaymentTracking } from '@/hooks/checkout/useCardPaymentTracking';
import { useGetAmplitudeExperimentVariant } from '@/hooks/useAmplitudeExperiment';
import useExternalScript from '@/hooks/useExternalScript';
import useIntersectionObserver from '@/hooks/useIntersectionObserver';
import { LoginContext } from '@/hooks/useLogin';
import useMobileView from '@/hooks/useMobileView';
import { usePaymentsHistory } from '@/hooks/usePaymentsHistory';
import { _s } from '@/locale';
import { _t } from '@/locale/i18n';
import { saveCofCheckoutStateToLocalStorage } from '@/pages/validate-cof-payment-redirect/ValidateCofPaymentRedirect.hooks';
import { bookServices } from '@/services';
import { AppointmentResponse, appointmentResponseSchema, ConfirmedBooking } from '@/types/api/services/booking';
import { CheckoutMissingAction, SelectedPaymentMethod, selectedPaymentMethodSchema } from '@/types/checkout';
import { PaymentCard } from '@/types/paymentcards';
import { BookState } from '@/types/state/book';
import * as Sentry from '@sentry/react';
import { Dispatch, useContext, useEffect, useReducer, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PrepayCheckoutProvider, usePrepayCheckout } from './PrepayCheckout.hooks';

type PrepayCheckoutWrapperAction =
  | {
      type: 'SET_BOOKING';
      payload: AppointmentResponse['booking'];
    }
  | {
      type: 'SET_ERROR';
      payload: boolean;
    };

type PrepayCheckoutWrapperState = {
  booking: AppointmentResponse['booking'];
  loading: boolean;
  error: boolean;
};

const baseTranslationKey = 'pages.booking.checkout.$id.PrepayCheckout' as const;

const PrepayCheckout = ({ booking }: { booking: ConfirmedBooking }) => {
  const {
    selectedPaymentMethod,
    summary,
    missingActions,
    cofThreeDS,
    submitting,
    swish,
    session,
    onDismissSwishQRCode,
    onCloseAdyenCheckoutModal,
    onSubmitting,
    onApplyGiftcard,
    onChangePaymentMethod,
    prepayCheckoutAPI,
    onRemoveGiftcard,
  } = usePrepayCheckout();
  const history = useHistory();
  // experiment with radio buttons
  const usePromotedPaymentMethods =
    useGetAmplitudeExperimentVariant()('promoted-payment-methods-web')?.value === 'variant-options-visible';
  // experiment with radio buttons and popular badge
  const useHighlightedPaymentMethods =
    useGetAmplitudeExperimentVariant()('highlighted-payment-methods')?.value === 'variant-popular-badge-visible';
  // experiment with new checkout redesign radio buttons

  const { onClose: onCheckoutModalClose, ...checkoutModalContext } = useCheckoutModal();
  const [klarnaVariant, setKlarnaVariant] = useState<KlarnaVariant | undefined>();
  const { isMobileView } = useMobileView();
  const user = useAppSelector((state) => state.users?.user);
  const loginContext = useContext(LoginContext);
  const bookState = useAppSelector((state) => state.book) as BookState;
  const addedGiftcards = bookState.usageReqId?.giftcards;
  const hasCoF = hasPaymentMethod(summary, CHECKOUT_PAYMENT_METHOD.STORED_COF);
  useExternalScript({ src: 'https://pay.google.com/gp/p/js/pay.js', cleanup: false, load: hasCoF });
  const addedGiftcardsList = Object.values(addedGiftcards || {}).map((giftcard) => giftcard);

  const confirmationContainerRef = useRef<HTMLDivElement>(null);
  const checkoutCtaPosition = useIntersectionObserver(confirmationContainerRef, { root: null, threshold: 0.3 });
  const isCheckoutCTAInView = checkoutCtaPosition === 'inView';
  const cardTracking = useCardPaymentTracking(
    { flow: 'payment_activation', company: { id: booking.place.id, name: booking.place.about.name } },
    checkoutModalContext,
  );

  const { manualSetPaymentMethods, paymentMethods, storedPaymentMethods, loading, isBokadirektPay, platformFee } =
    usePaymentMethodsContext();
  const klarnaBrands = getValidKlarnaBrands(
    paymentMethods,
    (summary?.finalWithoutGiftcards ?? 0) + (platformFee?.enabled ? platformFee.amount : 0),
  );

  const { paymentMethod, ...rest } = mapConfirmedBookingToCheckoutDetailsProps({
    booking,
    selectedPaymentMethod,
    addedGiftcards,
  });
  const services = mapConfirmedBookingInfoServices(booking);
  const giftcards = mapConfirmedBookingInfoGiftcards(booking, addedGiftcards, onRemoveGiftcard);
  const checkoutDetailsProps: CheckoutDetailsProps = { ...rest, services, giftcards };
  const showThreeDSecure = Boolean(cofThreeDS?.adyenActionJson);
  const pageUrl = `${url.getBaseUrl()}/booking/checkout/${booking.id}`;
  const pageTitle = _s(`seo.activateOnlinePaymentTitle`);
  const pageDescription = _s(`seo.activateOnlinePaymentDescription`);

  const showFloatingCTA = isCheckoutCTAInView !== undefined && Boolean(!isCheckoutCTAInView);
  const isKlarnaOrQliro =
    selectedPaymentMethod.type === CHECKOUT_PAYMENT_METHOD.QLIRO ||
    selectedPaymentMethod.type === CHECKOUT_PAYMENT_METHOD.KLARNA;

  const showSwishQrCode = Boolean(swish?.showQrCode);
  const showGlobalLoadingSpinner =
    submitting &&
    !(
      isBokadirektPay &&
      selectedPaymentMethod.type !== CHECKOUT_PAYMENT_METHOD.STORED_COF &&
      selectedPaymentMethod.type !== CHECKOUT_PAYMENT_METHOD.PAY_AT_PLACE
    );

  const checkoutTerms = getCheckoutTerms(
    'booking',
    getCheckoutTermsVariant(selectedPaymentMethod, isBokadirektPay),
    isKlarnaOrQliro
      ? getBookingSubmitLabel({
          paymentMethod: selectedPaymentMethod.type,
          isMobileView,
          isFloating: !isCheckoutCTAInView,
        })
      : _s('bookCTA.pay'),
    checkoutDetailsProps.placeName,
  );

  const handleChangePaymentMethod = (method: SelectedPaymentMethod) => {
    if (method.type === CHECKOUT_PAYMENT_METHOD.NEW_COF) {
      checkoutModalContext.onOpen('cof');

      return;
    }

    onChangePaymentMethod(method);
    cardTracking.trackPaymentMethodChange(selectedPaymentMethod, method);

    const buildSummaryProperties = getSummaryProperties({
      summary,
      selectedPaymentMethod: method,
      isBokadirektPay,
      hasPlatformFee: isBokadirektPay && (platformFee?.amount ?? 0) > 0,
    });
    paymentActivationFlowTrackingSession.setProperties(
      buildSummaryProperties({
        type: 'payment_activation_flow',
        data: { ...booking, usageReqId: bookState.usageReqId },
      }),
    );

    onCheckoutModalClose();
  };

  const handleOnAbortCheckoutModal = () => {
    cardTracking.trackCardEntryCancelled();
    onCheckoutModalClose();
  };

  const handleCloseCheckoutModal = () => {
    onCheckoutModalClose();
  };

  const handleCloseAdyenCheckoutModal = () => {
    onCloseAdyenCheckoutModal();
    onCheckoutModalClose();
  };

  const handleOnCardAdded = (card: PaymentCard) => {
    cardTracking.trackPaymentCardAdded(card);
    handleChangePaymentMethod({
      type: CHECKOUT_PAYMENT_METHOD.STORED_COF,
      brand: card.brand,
      id: card.id,
      lastFour: card.lastFour,
    });
  };

  const handleBeforeThreeDSRedirect = () => {
    const validatedAdyenPaymentMethod = selectedPaymentMethodSchema.safeParse(selectedPaymentMethod);

    if (validatedAdyenPaymentMethod.success === false) {
      Sentry.captureException(validatedAdyenPaymentMethod.error);
      return;
    }

    saveCofCheckoutStateToLocalStorage({
      isAddNewCard: true,
      selectedPaymentMethod: validatedAdyenPaymentMethod.data,
    });
  };

  const handleOnCompleteAdyenCheckout = useOnCompleteAdyenCheckout({
    session,
    resourceId: booking.id,
    storedPaymentMethods,
    paymentMethods,
    manualSetPaymentMethods,
    onSubmitting,
    onCardAdded: handleOnCardAdded,
    onNoNewCardFound: handleCloseAdyenCheckoutModal,
    onBeforeRedirect: handleBeforeThreeDSRedirect,
    onError: () => {
      handleCloseAdyenCheckoutModal();
      toast(({ closeToast }) => (
        <Snackbar label={_s(`${baseTranslationKey}.adyenCheckoutError`)} type="danger" onClose={closeToast} />
      ));
    },
    trackingSessionId: paymentActivationFlowTrackingSession.getId(),
  });

  const handleDismissSwishQRCode = () => {
    onDismissSwishQRCode();
  };

  const handleOnCheckoutTermsOpen = () => {
    paymentActivationFlowTrackingSession.terms('initiated', 'T&C_bd');
  };

  function handlePageLeave() {
    paymentActivationFlowTrackingSession.cancelSession();
  }

  const handleApplyGiftcard = (code: string) => {
    onApplyGiftcard(code)((props) => {
      cardTracking.trackCardApplied(props);
      if (props.success) {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
        handleCloseCheckoutModal();
      }
    });
  };

  /**
   * listen to history changes to detect when user navigates back
   */
  useEffect(() => {
    const unlisten = history.listen((_location, action) => {
      if (action === 'POP') {
        handlePageLeave();
      }
    });
    return unlisten;
  }, [history]);

  return (
    <CheckoutViewLayout
      header={{
        leftSlot: {
          button: {
            item: {
              identifier: HeaderButtonIdentifier.Back,
              icon: 'back',
            },
            'aria-label': _t('back'),
            onClick: () => history.go(-1),
          },
          text: _t('back'),
        },
        middleSlot: {
          title: _t(`${baseTranslationKey}.page-title`),
          subtitle: checkoutDetailsProps.placeName,
        },
        rightSlot: {
          ...(checkoutDetailsProps.logo && { imgUrl: checkoutDetailsProps.logo }),
        },
      }}
      wrapperClass={isMobileView ? 'bg-surface_l4' : 'bg-gradient'}>
      {showGlobalLoadingSpinner && <LoadingPlaceHolder className="fixed h-screen" />}
      <div className="bg-brown-50 lg:bg-transparent">
        <div className="lg:py-xxl lg:container">
          <div className="gap-xxl flex items-start">
            <div className="gap-lg flex grow-[9999] basis-[600px] flex-col">
              <CardWrapper className="w-full lg:hidden">
                <CheckoutDetails {...checkoutDetailsProps} />
              </CardWrapper>
              {!user && (
                <CardWrapper>
                  <UserLogin {...loginContext} initiatedFrom="checkout_prior_booking_summary" />
                </CardWrapper>
              )}
              {user && summary.canPayOnline && (
                <CardWrapper>
                  <PromotedPaymentMethods
                    summary={summary}
                    loading={isBokadirektPay && loading}
                    selectedPaymentMethod={selectedPaymentMethod}
                    onChangePaymentMethod={handleChangePaymentMethod}
                    triggerSource={SCREEN_NAME.CHECKOUT_BOOKING_UPDATE_PAYMENT_METHOD}
                    useHighlighted={useHighlightedPaymentMethods}
                    error={missingActions.includes('payment-method')}
                    {...checkoutModalContext}
                  />
                </CardWrapper>
              )}
              {user && (
                <>
                  <div ref={confirmationContainerRef}>
                    <CardWrapper>
                      <div className="gap-xl p-lg flex flex-col">
                        <SubmitCTA setKlarnaVariant={setKlarnaVariant} />
                        <CheckoutTerms
                          description={checkoutTerms.description}
                          linkLabel={checkoutTerms.linkLabel}
                          termsUrl={checkoutTerms.termsUrl}
                          onTermsOpen={handleOnCheckoutTermsOpen}
                        />
                      </div>
                    </CardWrapper>
                  </div>
                  <FloatingCTAWrapper
                    description={checkoutTerms.description}
                    linkLabel={checkoutTerms.linkLabel}
                    termsUrl={checkoutTerms.termsUrl}
                    show={showFloatingCTA && isMobileView}
                    onTermsOpen={handleOnCheckoutTermsOpen}>
                    <SubmitCTA floating setKlarnaVariant={setKlarnaVariant} />
                  </FloatingCTAWrapper>
                </>
              )}
            </div>
            <div className="lg:gap-lg sticky top-20 hidden lg:flex lg:grow-[100] lg:basis-[390px] lg:flex-col">
              <CardWrapper className="w-full">
                <CheckoutDetails key={2} {...checkoutDetailsProps} />
                {user && showFloatingCTA && !isMobileView && (
                  <div
                    className={classnames(
                      'gap-xl p-lg flex flex-col',
                      !checkoutDetailsProps.information &&
                        !checkoutDetailsProps.paymentMethod &&
                        'border-black-100 border-t',
                    )}>
                    <SubmitCTA setKlarnaVariant={setKlarnaVariant} />
                    <CheckoutTerms
                      description={checkoutTerms.description}
                      linkLabel={checkoutTerms.linkLabel}
                      termsUrl={checkoutTerms.termsUrl}
                      onTermsOpen={handleOnCheckoutTermsOpen}
                    />
                  </div>
                )}
              </CardWrapper>
            </div>
          </div>
        </div>
      </div>
      {checkoutModalContext.screen === 'klarnaOptions' && (
        <KlarnaOptionsModal
          isOpen={checkoutModalContext.isOpen}
          onClose={onCheckoutModalClose}
          brands={klarnaBrands}
          onSelect={async (variant) => {
            setKlarnaVariant(variant);
            checkoutModalContext.onOpen('bokadirektPay');
            const success = await prepayCheckoutAPI().submitAdyen();
            if (!success) onCheckoutModalClose();
          }}
        />
      )}
      {checkoutModalContext.screen === 'walleyPhoneInput' && (
        <WalleyPhoneModal
          isOpen={checkoutModalContext.isOpen}
          onClose={onCheckoutModalClose}
          onSubmit={async (phone) => {
            checkoutModalContext.onOpen('bokadirektPay');
            const success = await prepayCheckoutAPI().submitAdyen({ walleyPhone: phone });
            if (!success) onCheckoutModalClose();
          }}
        />
      )}
      {(checkoutModalContext.screen === 'bokadirektPay' || checkoutModalContext.screen === 'bokadirektAddCard') && (
        <AdyenCheckoutComponent
          session={session}
          onClose={handleCloseAdyenCheckoutModal}
          isOpen={checkoutModalContext.isOpen}
          submitting={submitting}
          amount={selectedPaymentMethod.final}
          onComplete={handleOnCompleteAdyenCheckout}
          paymentMethodType={
            checkoutModalContext.screen === 'bokadirektAddCard'
              ? CHECKOUT_PAYMENT_METHOD.NEW_COF
              : selectedPaymentMethod.type
          }
          klarnaVariant={checkoutModalContext.screen === 'bokadirektPay' ? klarnaVariant : undefined}
        />
      )}
      {checkoutModalContext.screen === 'cof' ? (
        <CreditCardModal
          onBeforeThreeDSRedirect={handleBeforeThreeDSRedirect}
          isOpen={checkoutModalContext.isOpen}
          onClose={handleCloseCheckoutModal}
          onCardAdded={handleOnCardAdded}
          returnPath={`/booking/checkout/${booking.id}/validate-3ds-redirect`}
          traceTrackingSession={{ payment_activation_session_id: paymentActivationFlowTrackingSession.getId() }}
          {...checkoutModalContext}
        />
      ) : (
        checkoutModalContext.screen !== 'bokadirektPay' &&
        checkoutModalContext.screen !== 'bokadirektAddCard' &&
        checkoutModalContext.screen !== 'klarnaOptions' &&
        checkoutModalContext.screen !== 'walleyPhoneInput' && (
          <CheckoutModal
            selectedPaymentMethod={selectedPaymentMethod}
            onChangePaymentMethod={handleChangePaymentMethod}
            onClose={handleOnAbortCheckoutModal}
            onApplyGiftcard={handleApplyGiftcard}
            {...checkoutModalContext}
          />
        )
      )}
      {showThreeDSecure && (
        <Modal isOpen={!submitting}>
          <Modal.Content floating={!isMobileView}>
            <div className={classnames('gap-xl pb-lg px-lg flex flex-col', isMobileView && 'px-lg')}>
              <ThreeDSComponent
                action={JSON.parse(cofThreeDS?.adyenActionJson)}
                handleOnAdditionalDetails={(data) =>
                  prepayCheckoutAPI().submitThreeDS(
                    data,
                    selectedPaymentMethod.type === CHECKOUT_PAYMENT_METHOD.GOOGLE_PAY,
                  )
                }
              />
            </div>
          </Modal.Content>
        </Modal>
      )}
      {showSwishQrCode && <SwishModal onClose={handleDismissSwishQRCode} qrCode={swish.payment.qrCode} />}

      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={pageUrl} />

        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta name="robots" content="noindex" />
      </Helmet>
    </CheckoutViewLayout>
  );
};

const SubmitCTA = ({
  floating = false,
  setKlarnaVariant,
}: {
  floating?: boolean;
  setKlarnaVariant: (variant: KlarnaVariant) => void;
}) => {
  const {
    isBokadirektPay,
    paymentMethods: submitPaymentMethods,
    platformFee: submitPlatformFee,
  } = usePaymentMethodsContext();
  const { isMobileView } = useMobileView();
  const checkoutModalContext = useCheckoutModal();
  const {
    selectedPaymentMethod,
    submitting,
    summary,
    missingActions,
    prepayCheckoutAPI,
    onCheckoutMissingAction,
    bookingPhone,
  } = usePrepayCheckout();
  const klarnaBrands = getValidKlarnaBrands(
    submitPaymentMethods,
    (summary?.finalWithoutGiftcards ?? 0) + (submitPlatformFee?.enabled ? submitPlatformFee.amount : 0),
  );
  const user = useAppSelector((state) => state.users?.user);
  const bookState = useAppSelector((state) => state.book) as BookState;
  const paymentMethod = selectedPaymentMethod.type;
  const isKlarnaOrQliro =
    paymentMethod === CHECKOUT_PAYMENT_METHOD.QLIRO || paymentMethod === CHECKOUT_PAYMENT_METHOD.KLARNA;
  const { final } = summary.availablePaymentMethods.find((method) => method.type === paymentMethod) || {};
  const invalidPaymentMethod = paymentMethod === CHECKOUT_PAYMENT_METHOD.NONE;
  const appliedGiftcards = Object.values(bookState.usageReqId?.giftcards ?? {});
  const isFullyPaidWithGiftcard = isFullyPaidWithGiftcards(summary, appliedGiftcards);

  const ctaIconVariant = getCheckoutSubmitIconVariantIfAny(
    selectedPaymentMethod,
    isFullyPaidWithGiftcard,
    appliedGiftcards,
  );

  const buttonProps: ButtonProps = {
    size: 'lg',
    block: true,
    disabled: submitting || Boolean(missingActions.length),
    label: isKlarnaOrQliro
      ? getBookingSubmitLabel({ paymentMethod: paymentMethod, isFloating: floating, isMobileView: isMobileView })
      : _s('bookCTA.pay'),
    leftIcon: ctaIconVariant ? <Icon variant={ctaIconVariant} noFilter /> : null,
    iconNoFilter: Boolean(ctaIconVariant),
    onClick: async () => {
      paymentActivationFlowTrackingSession.bookingCTAClicked();

      const missingActions: CheckoutMissingAction[] = [];
      if (invalidPaymentMethod) missingActions.push('payment-method');

      missingActions.forEach((action) => onCheckoutMissingAction(action));

      if (missingActions.length) {
        paymentActivationFlowTrackingSession.validationError(
          (() => {
            let errors = [];
            if (missingActions.includes('participants')) errors.push('missing_participants');
            if (missingActions.includes('practitioner-terms')) errors.push('merchant_terms');
            if (missingActions.includes('payment-method')) errors.push('payment_methods');
            if (missingActions.includes('email')) errors.push('email_missing');
            return errors;
          })(),
        );
        return;
      }

      if (isBokadirektPay && paymentMethod !== CHECKOUT_PAYMENT_METHOD.PAY_AT_PLACE) {
        if (paymentMethod === CHECKOUT_PAYMENT_METHOD.KLARNA) {
          if (klarnaBrands.length === 1) {
            setKlarnaVariant(klarnaBrands[0]);
            checkoutModalContext.onOpen('bokadirektPay');
            const success = await prepayCheckoutAPI().submitAdyen();
            if (!success) checkoutModalContext.onClose();
            return;
          }
          return checkoutModalContext.onOpen('klarnaOptions');
        }
        if (paymentMethod === CHECKOUT_PAYMENT_METHOD.WALLEY) {
          const userPhone = user?.contact?.mobile || user?.contact?.phone;
          const hasValidUserPhone = userPhone ? validatePhoneNumber(userPhone) : false;
          if (!hasValidUserPhone && !bookingPhone) {
            return checkoutModalContext.onOpen('walleyPhoneInput');
          }
          checkoutModalContext.onOpen('bokadirektPay');
          const phoneToSend = bookingPhone || (hasValidUserPhone ? userPhone : undefined);
          const success = await prepayCheckoutAPI().submitAdyen({ walleyPhone: phoneToSend });
          if (!success) checkoutModalContext.onClose();
          return;
        }
        if (paymentMethod !== CHECKOUT_PAYMENT_METHOD.STORED_COF) {
          checkoutModalContext.onOpen('bokadirektPay');
        }
        const success = await prepayCheckoutAPI().submitAdyen();
        if (!success) checkoutModalContext.onClose();
        return;
      }

      switch (paymentMethod) {
        case CHECKOUT_PAYMENT_METHOD.KLARNA:
          return prepayCheckoutAPI().initKlarna();
        case CHECKOUT_PAYMENT_METHOD.QLIRO:
          return prepayCheckoutAPI().initQliro();
        case CHECKOUT_PAYMENT_METHOD.STORED_COF:
          return prepayCheckoutAPI().submitCoF(selectedPaymentMethod.id);
        case CHECKOUT_PAYMENT_METHOD.GOOGLE_PAY:
          return prepayCheckoutAPI().submitGooglePay(+final);
        case CHECKOUT_PAYMENT_METHOD.APPLE_PAY:
          return prepayCheckoutAPI().submitApplePay(+final);
        case CHECKOUT_PAYMENT_METHOD.SWISH:
          return prepayCheckoutAPI().submitSwish();
        case CHECKOUT_PAYMENT_METHOD.PAY_AT_PLACE:
          return prepayCheckoutAPI().submitPayAtPlace();
        default:
          return null;
      }
    },
  };

  return <Button {...buttonProps} />;
};

async function fetchAndSetBooking(bookingId: number, dispatch: Dispatch<PrepayCheckoutWrapperAction>) {
  const { data, error } = await promiseWrapper(bookServices.appointment(bookingId));

  const validateData = appointmentResponseSchema.safeParse(data);

  if (!validateData.success || error) {
    dispatch({ type: 'SET_ERROR', payload: true });
    return null;
  }

  dispatch({ type: 'SET_BOOKING', payload: validateData.data.booking });
  return validateData.data.booking;
}

/**
 * Wrap the checkout page in a component that checks if the book state is valid
 * and other validation that needs to be done before rendering the checkout page.
 */
// eslint-disable-next-line import/no-anonymous-default-export
export default withWalletPayment(function () {
  const routeMatch = useRouteMatch();
  const location = useLocation();
  const history = useHistory();
  const user = useAppSelector((state) => state.users?.user);
  // The cards endpoint authenticates off the session token, which outlives a late or failed profile fetch.
  // The profile selector stays so a mid-checkout login re-renders this.
  const isLoggedIn = !!user || !!loggedUser();
  const { cards, loading: cardsLoading } = useCardsManager([], isLoggedIn);
  const { bookingId } = routeMatch.params as { bookingId: string };
  let { paymentHistory, loading: loadingPaymentHistory } = usePaymentsHistory();

  const [state, dispatch] = useReducer(
    (state: PrepayCheckoutWrapperState, action: PrepayCheckoutWrapperAction) => {
      switch (action.type) {
        case 'SET_BOOKING':
          return {
            ...state,
            booking: action.payload,
            error: false,
            loading: false,
          };
        case 'SET_ERROR': {
          return {
            ...state,
            error: action.payload,
            loading: false,
          };
        }
        default:
          return state;
      }
    },
    {
      booking: null,
      loading: true,
      error: false,
    },
  );

  useEffect(() => {
    fetchAndSetBooking(Number(bookingId), dispatch);
  }, [bookingId]);

  useEffect(() => {
    const threeDSecureRedirectResultSuccess = getThreeDSecureRedirectResultIfAny(location, history);
    if (threeDSecureRedirectResultSuccess || threeDSecureRedirectResultSuccess === undefined) return;
    toast(({ closeToast }) => (
      <Snackbar label={_s(`${baseTranslationKey}.threeDSecureRedirectError`)} type="danger" onClose={closeToast} />
    ));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (state.loading || cardsLoading || loadingPaymentHistory) return <LoadingPlaceHolder />;
  if (state.error || state.booking?.extra?.paymentMethod !== CHECKOUT_PAYMENT_METHOD.PAY_AT_PLACE) {
    paymentActivationFlowTrackingSession.error(['summary_error']);
    return <GoBack />;
  }

  const employeeId = state.booking.services[0]?.employee?.id;
  const placeId = state.booking.place?.id;
  const isBokadirektPay = isBokadirektPayCheckout(state.booking.services[0]?.employee);

  return (
    <UsePaymentMethodsProvider
      employeeId={employeeId}
      placeId={placeId}
      fetchOnMount={isBokadirektPay}
      isBokadirektPay={isBokadirektPay}>
      <WithPaymentMethodsLoaded>
        <UseCardsProvider initialCards={cards} fetchOnMount={!isBokadirektPay && isLoggedIn}>
          <PrepayCheckoutProvider booking={state.booking} paymentHistory={paymentHistory}>
            <CheckoutModalProvider>
              <PrepayCheckout booking={state.booking} />
            </CheckoutModalProvider>
          </PrepayCheckoutProvider>
        </UseCardsProvider>
      </WithPaymentMethodsLoaded>
    </UsePaymentMethodsProvider>
  );
});
